import { Injectable, signal } from "@angular/core";
import { BpeService } from "../../bpe/bpe.service";
import { EventData } from "../../../services/event.service";
import {
    runDeferredEventDetailsInlineEditScenarios,
    runEventDetailsInlineEditScenarios,
} from "./event.details.inline.edit.bpe.util";

@Injectable()
export class S25EventDetailsInlineEditService {
    /** When true, event details inline saves skip event save email scenarios. */
    readonly silentSave = signal(true);

    /** Event data captured when silent save is enabled, before batch edits. */
    readonly deferredScenarioPreData = signal<Promise<EventData | undefined> | undefined>(undefined);

    /** True after at least one silent save since deferredScenarioPreData was captured. */
    readonly hasPendingScenarioChanges = signal(false);

    /** True while deferred post-save scenarios are running. */
    readonly runningDeferredScenarios = signal(false);

    runInlineEditScenarios<T>(eventId: number, change: () => Promise<T>): Promise<T> {
        const silent = this.silentSave();
        if (silent) {
            if (!this.deferredScenarioPreData()) {
                this.captureDeferredScenarioPreData(eventId);
            }
            this.hasPendingScenarioChanges.set(true);
        }
        return runEventDetailsInlineEditScenarios(eventId, change, silent);
    }

    captureDeferredScenarioPreData(eventId: number, eventData?: EventData): void {
        this.deferredScenarioPreData.set(eventData ? Promise.resolve(eventData) : BpeService.getEventData(eventId));
    }

    clearDeferredScenarioState(): void {
        this.deferredScenarioPreData.set(undefined);
        this.hasPendingScenarioChanges.set(false);
    }

    initForEventDetailsInlineEdit(eventId: number): void {
        this.silentSave.set(true);
        this.captureDeferredScenarioPreData(eventId);
    }

    setSilentSave(eventId: number, silentSave: boolean): void {
        this.silentSave.set(silentSave);
        if (silentSave) {
            this.captureDeferredScenarioPreData(eventId);
        } else {
            this.clearDeferredScenarioState();
        }
    }

    async runDeferredInlineEditScenarios(eventId: number): Promise<void> {
        if (this.runningDeferredScenarios()) {
            return;
        }
        const preDataPromise = this.deferredScenarioPreData();
        if (!preDataPromise || !this.hasPendingScenarioChanges()) {
            return;
        }
        const preEventData = await preDataPromise;
        if (!preEventData) {
            return;
        }

        this.hasPendingScenarioChanges.set(false);
        this.deferredScenarioPreData.set(undefined);
        this.runningDeferredScenarios.set(true);
        try {
            const postEventData = await runDeferredEventDetailsInlineEditScenarios(eventId, preEventData);
            if (!this.hasPendingScenarioChanges()) {
                this.captureDeferredScenarioPreData(eventId, postEventData);
            }
        } finally {
            this.runningDeferredScenarios.set(false);
        }
    }
}
